import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "app-header flex md:flex-row flex-column justify-content-between" }
const _hoisted_2 = { class: "header-left" }
const _hoisted_3 = { class: "mb-0" }
const _hoisted_4 = { class: "mt-0" }
const _hoisted_5 = {
  key: 0,
  class: "header-right flex flex-row gap-2"
}
const _hoisted_6 = { class: "stat-div" }
const _hoisted_7 = { class: "stat-div" }
const _hoisted_8 = { class: "app-inner-container" }

import { Doctor } from '@/interfaces/Doctor';
import { Seller } from '@/interfaces/Seller';
import { Patient } from '@/interfaces/Patient';
import { Consultation } from '@/interfaces/Consultation';
import DoctorService from '@/services/DoctorService';
import PatientsService from '@/services/PatientsService';
import Card from 'primevue/card';
import DoctorList from '@/components/DoctorList.vue';
import { onMounted, ref, Ref, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useRouter } from 'vue-router';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import ConfirmDialog from 'primevue/confirmdialog';
import Toast from 'primevue/toast';
import Button from 'primevue/button';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import PatientList from '@/components/PatientList.vue';
import ConsultationList from '@/components/ConsultationList.vue';
import ConsultationService from '@/services/ConsultationService';


export default /*#__PURE__*/_defineComponent({
  __name: 'PatientDetailsView',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const confirm = useConfirm();
const toast = useToast();
const patientId = route.params.id as string;

const patient: Ref<Patient | null> = ref(null as Patient | null);
const doctors: Ref<Doctor[]> = ref([] as Doctor[]);
const patients: Ref<Patient[]> = ref([] as Patient[]);
const consultations: Ref<Consultation[]> = ref([] as Consultation[]);
const selectedDoctor = ref<Doctor | null>(null);
const loadingConsultations = ref(false);
const loadingDots = ref('');
const activeTab = ref(1);

const safeDisplay = (value?: string | null): string => value ?? '';

const onTabChange = (event: { index: number }) => {
  //alert('onmounted.onTabChange');
  activeTab.value = event.index;

  if (event.index === 1) {
    router.replace({ query: {} });
  }
  if (event.index === 0) {
    router.replace({ path: '/patients' });
  }
};

const headerTitle = computed(() => {
  return `${safeDisplay(patient.value?.firstName)} ${safeDisplay(patient.value?.lastName)}`;
});

const headerSubtitle = computed(() => {
  if (route.query.refId && selectedDoctor.value) {
    return 'Consultatii ' + [
      safeDisplay(selectedDoctor.value.title),
      safeDisplay(selectedDoctor.value.firstName),
      safeDisplay(selectedDoctor.value.lastName)
    ].filter(Boolean).join(' ');
  }
  return 'Detalii generale';
});

watch(
  () => route.params.id,
  async (newId) => {
    if (!newId) return;

    try {
      loadingConsultations.value = true;
      startLoadingDots();
      const fetchedPatient = await PatientsService.fetchPatient(patientId);
      if (fetchedPatient) {
        patient.value = fetchedPatient;      
      }
      const fetchedDoctors = await DoctorService.fetchDoctorsByPatientId(patientId);
      if (fetchedDoctors && Array.isArray(fetchedDoctors)) {
        doctors.value = fetchedDoctors;
      }
      const fetchedPatients = await PatientsService.fetchPatients();
      if (fetchedPatients && Array.isArray(fetchedPatients)) {
        patients.value = fetchedPatients;
      }
      const fetchedConsultations = await ConsultationService.fetchConsultationsOfUser(patientId);
      if (fetchedConsultations && Array.isArray(fetchedConsultations)) {
        consultations.value = fetchedConsultations;
      }
      //alert('onmounted.watch');
      activeTab.value = 1;
    } catch (error) {
      console.log(error);
    } finally {
      loadingConsultations.value = false;
      stopLoadingDots();
    }
  }
);

watch(
  () => route.query.refId,
  async (newId) => {
    if (!newId) {
      selectedDoctor.value = null;
      return;
    }

    loadingConsultations.value = true;
    startLoadingDots();
      
    try {
      const fetchedConsultations = await ConsultationService.fetchConsultationsOfDoctorAndPatient(newId as string, patientId);
      if (fetchedConsultations && Array.isArray(fetchedConsultations)) {
        consultations.value = fetchedConsultations;
      }
      const doctor = doctors.value.find(d => d.id === newId);
      if (doctor) {
        selectedDoctor.value = doctor;
      }
      activeTab.value = 2;
    } catch (error) {
      console.log(error);
    } finally {
      loadingConsultations.value = false;
      stopLoadingDots();
    }
  }
);

let dotsInterval: number | null = null;

const startLoadingDots = () => {
  loadingDots.value = '';
  if (dotsInterval) clearInterval(dotsInterval);
  dotsInterval = setInterval(() => {
    loadingDots.value = loadingDots.value.length < 3 ? loadingDots.value + '.' : '';
  }, 500);
};

const stopLoadingDots = () => {
  if (dotsInterval) {
    clearInterval(dotsInterval);
    dotsInterval = null;
  }
};

const blockPatientConfirmation = () => {
  confirm.require({
    message: 'Sunteți sigur că doriți să blocați contul pacientului?',
    header: 'Confirmare',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Da',
    rejectLabel: 'Nu',
    accept: () => {
      blockPatient();
    }
  });
};

const unblockPatientConfirmation = () => {
  confirm.require({
    message: 'Sunteți sigur că doriți să deblocați contul pacientului?',
    header: 'Confirmare',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Da',
    rejectLabel: 'Nu',
    accept: () => {
      unblockPatient();
    }
  });
};

const deletePatientConfirmation = () => {
  confirm.require({
    message: 'Atenție! Această acțiune este ireversibilă! Sunteți sigur că doriți să ștergeți contul pacientului?',
    header: 'Confirmare',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Da',
    rejectLabel: 'Nu',
    accept: () => {
      deletePatient();
    }
  });
};

const blockPatient = async () => {
  try {
    await PatientsService.suspendPatient(patientId);
    showSuccess();
    location.reload();
  } catch (err) {
    if (err instanceof Error) {
      showError(err.message);
    } else {
      showError('A apărut o eroare la blocarea utilizatorului.');
    }
  }
};

const unblockPatient = async () => {
  try {
    await PatientsService.reactivatePatient(patientId);
    showSuccess();
    location.reload();
  } catch (err) {
    if (err instanceof Error) {
      showError(err.message);
    } else {
      showError('A apărut o eroare la deblocarea utilizatorului.');
    }
  }
};

const deletePatient = async () => {
  try {
    await PatientsService.deletePatient(patientId);
    showSuccess();
    location.reload();
  } catch (err) {
    if (err instanceof Error) {
      showError(err.message);
    } else {
      showError('A apărut o eroare la stergerea utilizatorului.');
    }
  }
};

const showError = (message: string): void => {
  toast.add({
    severity: 'error',
    summary: 'A apărut o eroare!',
    detail: message,
    life: 3000
  });
};

const showSuccess = (): void => {
  toast.add({
    severity: 'success',
    summary: 'Acțiune realizată cu succes!',
    detail: 'Utilizatorul a fost actualizat cu succes',
    life: 3000
  });
};

onMounted(async () => {
  try {
    loadingConsultations.value = true;
    startLoadingDots();
    const fetchedPatient = await PatientsService.fetchPatient(patientId);
    if (fetchedPatient) {
      patient.value = fetchedPatient;      
    }
    const fetchedDoctors = await DoctorService.fetchDoctorsByPatientId(patientId);
    if (fetchedDoctors && Array.isArray(fetchedDoctors)) {
      doctors.value = fetchedDoctors;
    }
    const fetchedPatients = await PatientsService.fetchPatients();
    if (fetchedPatients && Array.isArray(fetchedPatients)) {
      patients.value = fetchedPatients;
    }
    const fetchedConsultations = await ConsultationService.fetchConsultationsOfUser(patientId);
    if (fetchedConsultations && Array.isArray(fetchedConsultations)) {
      consultations.value = fetchedConsultations;
    }
    //alert('onmounted.tabselect');
    //activeTab.value = 1;
  } catch (error) {
    console.log(error);
  } finally {
    loadingConsultations.value = false;
    stopLoadingDots();
  }
});

return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(Toast)),
    _createVNode(_unref(ConfirmDialog)),
    _createVNode(_unref(Card), null, {
      title: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("h1", _hoisted_3, _toDisplayString(headerTitle.value), 1),
            _createElementVNode("h5", _hoisted_4, _toDisplayString(headerSubtitle.value), 1)
          ]),
          (patient.value?.status != 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createElementVNode("div", _hoisted_6, [
                  (patient.value?.status == 2)
                    ? _withDirectives((_openBlock(), _createBlock(_unref(Button), {
                        key: 0,
                        onClick: _cache[0] || (_cache[0] = ($event: any) => (unblockPatientConfirmation())),
                        class: "p-button-md",
                        outlined: "",
                        severity: "success",
                        icon: "pi pi-unlock"
                      }, null, 512)), [
                        [
                          _directive_tooltip,
                          {
                value: 'Deblocati utlizatorul',
                showDelay: 100,
                hideDelay: 300
              },
                          void 0,
                          { left: true }
                        ]
                      ])
                    : _withDirectives((_openBlock(), _createBlock(_unref(Button), {
                        key: 1,
                        onClick: _cache[1] || (_cache[1] = ($event: any) => (blockPatientConfirmation())),
                        class: "p-button-md text-red-500",
                        outlined: "",
                        severity: "warning",
                        icon: "pi pi-ban"
                      }, null, 512)), [
                        [
                          _directive_tooltip,
                          {
                value: 'Blocati utlizatorul',
                showDelay: 100,
                hideDelay: 300
              },
                          void 0,
                          { left: true }
                        ]
                      ])
                ]),
                _createElementVNode("div", _hoisted_7, [
                  _withDirectives(_createVNode(_unref(Button), {
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (deletePatientConfirmation())),
                    class: "p-button-md text-white",
                    severity: "danger",
                    icon: "pi pi-trash"
                  }, null, 512), [
                    [
                      _directive_tooltip,
                      {
                value: 'Stergeti utlizatorul',
                showDelay: 100,
                hideDelay: 300
              },
                      void 0,
                      { left: true }
                    ]
                  ])
                ])
              ]))
            : _createCommentVNode("", true)
        ])
      ]),
      content: _withCtx(() => [
        _createElementVNode("div", _hoisted_8, [
          _createVNode(_unref(TabView), {
            activeIndex: activeTab.value,
            "onUpdate:activeIndex": _cache[3] || (_cache[3] = ($event: any) => ((activeTab).value = $event)),
            onTabChange: onTabChange
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(TabPanel), { header: "Pacienti" }, {
                default: _withCtx(() => [
                  _createVNode(PatientList, { patients: patients.value }, null, 8, ["patients"])
                ]),
                _: 1
              }),
              _createVNode(_unref(TabPanel), { header: "Medici" }, {
                default: _withCtx(() => [
                  _createVNode(DoctorList, {
                    doctors: doctors.value,
                    tabOnClick: "patients",
                    refId: _unref(patientId)
                  }, null, 8, ["doctors", "refId"])
                ]),
                _: 1
              }),
              _createVNode(_unref(TabPanel), {
                header: `Consultatii${_unref(route).query.refId ? ' *' : ''}${loadingConsultations.value ? loadingDots.value : ''}`
              }, {
                default: _withCtx(() => [
                  _createVNode(ConsultationList, {
                    consultations: consultations.value,
                    mode: "patient",
                    doctors: doctors.value
                  }, null, 8, ["consultations", "doctors"])
                ]),
                _: 1
              }, 8, ["header"])
            ]),
            _: 1
          }, 8, ["activeIndex"])
        ])
      ]),
      _: 1
    })
  ], 64))
}
}

})